import { useState } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip
} from '@mui/material';
import {
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  Home as HomeIcon
} from '@mui/icons-material';

/**
 * FolderTreeSelect
 * Selector dropdown con estructura jerárquica de carpetas.
 *
 * Props:
 *   flatFolders   — array de { id, name, depth } del hook useFolders
 *   value         — folder_id seleccionado (null = raíz)
 *   onChange      — (folderId: string | null) => void
 *   officeColor   — color del despacho
 *   label         — label del select
 *   disabled      — boolean
 */
export default function FolderTreeSelect({
  flatFolders = [],
  value = null,
  onChange,
  officeColor = '#1976d2',
  label = 'Carpeta destino',
  disabled = false,
  size = 'medium'
}) {
  const selectedFolder = flatFolders.find(f => f.id === value);

  return (
    <FormControl fullWidth size={size} disabled={disabled}>
      <InputLabel sx={{ '&.Mui-focused': { color: officeColor } }}>
        {label}
      </InputLabel>
      <Select
        value={value ?? '__root__'}
        label={label}
        onChange={(e) => onChange(e.target.value === '__root__' ? null : e.target.value)}
        sx={{
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: officeColor },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: officeColor },
        }}
        renderValue={(val) => {
          if (val === '__root__') {
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HomeIcon sx={{ fontSize: 18, color: officeColor }} />
                <Typography variant="body2">Raíz del expediente</Typography>
              </Box>
            );
          }
          const folder = flatFolders.find(f => f.id === val);
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FolderOpenIcon sx={{ fontSize: 18, color: officeColor }} />
              <Typography variant="body2">{folder?.name || val}</Typography>
            </Box>
          );
        }}
      >
        {/* Opción raíz */}
        <MenuItem value="__root__">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HomeIcon sx={{ fontSize: 18, color: officeColor }} />
            <Typography variant="body2" fontWeight="medium">
              Raíz del expediente
            </Typography>
          </Box>
        </MenuItem>

        {/* Carpetas con indentación */}
        {flatFolders.map(folder => (
          <MenuItem
            key={folder.id}
            value={folder.id}
            sx={{ pl: `${(folder.depth + 1) * 16 + 16}px` }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FolderIcon sx={{ fontSize: 17, color: officeColor, opacity: 0.8 }} />
              <Typography variant="body2">{folder.name}</Typography>
              {folder.depth === 0 && (
                <Chip
                  label="carpeta"
                  size="small"
                  sx={{ height: 14, fontSize: '0.6rem', opacity: 0.5 }}
                />
              )}
            </Box>
          </MenuItem>
        ))}

        {flatFolders.length === 0 && (
          <MenuItem disabled>
            <Typography variant="caption" color="text.secondary">
              No hay carpetas creadas aún
            </Typography>
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
}